import React, { useCallback, useEffect, useMemo, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { toast } from "react-toastify";
import {
  LuChevronLeft,
  LuChevronRight,
  LuCircleCheck,
  LuClipboardList,
  LuClock3,
  LuEye,
  LuHourglass,
  LuMessageSquareHeart,
  LuSearch,
  LuStar,
} from "react-icons/lu";
import {
  FEEDBACK_STATUS_OPTIONS,
  FEEDBACK_TYPE_OPTIONS,
  feedbackExtractMessage,
  fetchMyFeedbackList,
  fetchMyFeedbackStats,
} from "../../services/platformFeedback.service";
import "../../assets/styles/my_disputes.scss";

const PAGE_OPTIONS = [10, 20, 50];

function formatDate(value) {
  if (!value) return "—";
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return "—";
  return date.toLocaleDateString("en-US", {
    month: "2-digit",
    day: "2-digit",
    year: "numeric",
  });
}

function formatTime(value) {
  if (!value) return "";
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return "";
  return date.toLocaleTimeString("en-US", {
    hour: "2-digit",
    minute: "2-digit",
    hour12: true,
  });
}

function formatStatus(status, label) {
  if (label) return label;
  return String(status || "")
    .replace(/_/g, " ")
    .replace(/\b\w/g, (char) => char.toUpperCase());
}

function truncateText(value, max = 72) {
  const text = String(value || "").trim();
  if (!text) return "—";
  if (text.length <= max) return text;
  return `${text.slice(0, max - 1)}…`;
}

function statusClass(status) {
  const normalized = String(status || "").toLowerCase();
  return `md-pill md-status md-status--${normalized.replace(/\s+/g, "_")}`;
}

function buildPageNumbers(currentPage, totalPages, maxVisible = 5) {
  if (totalPages <= maxVisible) {
    return Array.from({ length: totalPages }, (_, index) => index + 1);
  }
  let start = Math.max(1, currentPage - Math.floor(maxVisible / 2));
  let end = Math.min(totalPages, start + maxVisible - 1);
  start = Math.max(1, end - maxVisible + 1);
  return Array.from({ length: end - start + 1 }, (_, index) => start + index);
}

function renderStars(rating) {
  const value = Math.max(0, Math.min(5, Math.trunc(Number(rating) || 0)));
  return (
    <span className="md-rating-stars" title={`${value} / 5`}>
      {Array.from({ length: 5 }, (_, index) => (
        <LuStar
          key={index}
          aria-hidden
          className={index < value ? "is-filled" : ""}
        />
      ))}
    </span>
  );
}

export default function MyFeedbackPage() {
  const navigate = useNavigate();
  const [query, setQuery] = useState("");
  const [status, setStatus] = useState("");
  const [feedbackType, setFeedbackType] = useState("");
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(10);
  const [items, setItems] = useState([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(true);
  const [stats, setStats] = useState({
    total: 0,
    inProgress: 0,
    resolved: 0,
    closed: 0,
  });

  const loadStats = useCallback(async () => {
    try {
      const data = await fetchMyFeedbackStats();
      setStats({
        total: Math.trunc(Number(data.total) || 0),
        inProgress: Math.trunc(Number(data.inProgress) || 0),
        resolved: Math.trunc(Number(data.resolved) || 0),
        closed: Math.trunc(Number(data.closed) || 0),
      });
    } catch {
      // Keep prior stats.
    }
  }, []);

  const loadFeedback = useCallback(async () => {
    setLoading(true);
    try {
      const data = await fetchMyFeedbackList({
        search: query.trim() || undefined,
        status: status || undefined,
        feedbackType: feedbackType || undefined,
        page,
        pageSize,
      });
      setItems(Array.isArray(data.items) ? data.items : []);
      setTotal(Math.trunc(Number(data.total) || 0));
    } catch (error) {
      toast.error(feedbackExtractMessage(error, "Failed to load your feedback."));
      setItems([]);
      setTotal(0);
    } finally {
      setLoading(false);
    }
  }, [feedbackType, page, pageSize, query, status]);

  useEffect(() => {
    loadStats();
  }, [loadStats]);

  useEffect(() => {
    const timer = setTimeout(loadFeedback, 250);
    return () => clearTimeout(timer);
  }, [loadFeedback]);

  const totalPages = Math.max(1, Math.ceil(total / pageSize));
  const currentPage = Math.min(page, totalPages);
  const pageNumbers = useMemo(
    () => buildPageNumbers(currentPage, totalPages),
    [currentPage, totalPages],
  );

  const resetFilters = () => {
    setQuery("");
    setStatus("");
    setFeedbackType("");
    setPage(1);
  };

  const openDetail = (row) => {
    navigate(`/dashboard/help-center/my-feedback/${row.id}`);
  };

  return (
    <div className="md-page">
      <header className="md-head">
        <div>
          <nav className="md-breadcrumb" aria-label="Breadcrumb">
            <Link to="/home">Home</Link>
            <span aria-hidden>&gt;</span>
            <Link to="/help-center">Help Center</Link>
            <span aria-hidden>&gt;</span>
            <span aria-current="page">My Feedback</span>
          </nav>
          <h1>My Feedback</h1>
          <p>Track suggestions, compliments, and issues you have submitted to Trocanet.</p>
        </div>
        <div className="md-head__actions">
          <button
            type="button"
            className="md-btn md-btn--primary"
            onClick={() => navigate("/help-center/feedback")}
          >
            Submit Feedback
          </button>
        </div>
      </header>

      <section className="md-stats">
        <article className="md-stat">
          <span className="md-stat__icon md-stat__icon--blue">
            <LuClipboardList aria-hidden />
          </span>
          <div>
            <strong>{stats.total.toLocaleString()}</strong>
            <span>Total Feedback</span>
            <small>All time</small>
          </div>
        </article>
        <article className="md-stat">
          <span className="md-stat__icon md-stat__icon--orange">
            <LuHourglass aria-hidden />
          </span>
          <div>
            <strong>{stats.inProgress.toLocaleString()}</strong>
            <span>In Progress</span>
            <small>Under review</small>
          </div>
        </article>
        <article className="md-stat">
          <span className="md-stat__icon md-stat__icon--green">
            <LuCircleCheck aria-hidden />
          </span>
          <div>
            <strong>{stats.resolved.toLocaleString()}</strong>
            <span>Resolved</span>
            <small>Action completed</small>
          </div>
        </article>
        <article className="md-stat">
          <span className="md-stat__icon md-stat__icon--purple">
            <LuClock3 aria-hidden />
          </span>
          <div>
            <strong>{stats.closed.toLocaleString()}</strong>
            <span>Closed</span>
            <small>No further action</small>
          </div>
        </article>
      </section>

      <div className="md-toolbar">
        <label className="md-search">
          <LuSearch aria-hidden />
          <input
            value={query}
            onChange={(event) => {
              setQuery(event.target.value);
              setPage(1);
            }}
            placeholder="Search by Feedback ID, Subject..."
          />
        </label>
        <select
          value={status}
          onChange={(event) => {
            setStatus(event.target.value);
            setPage(1);
          }}
        >
          {FEEDBACK_STATUS_OPTIONS.map((opt) => (
            <option key={opt.value || "all-status"} value={opt.value}>
              {opt.label}
            </option>
          ))}
        </select>
        <select
          value={feedbackType}
          onChange={(event) => {
            setFeedbackType(event.target.value);
            setPage(1);
          }}
        >
          {FEEDBACK_TYPE_OPTIONS.map((opt) => (
            <option key={opt.value || "all-type"} value={opt.value}>
              {opt.label}
            </option>
          ))}
        </select>
        <button type="button" className="md-btn md-btn--ghost" onClick={resetFilters}>
          Reset
        </button>
      </div>

      <section className="md-card">
        <div className="md-table-wrap">
          <table className="md-table">
            <thead>
              <tr>
                <th>Feedback ID</th>
                <th>Subject</th>
                <th>Type</th>
                <th>Rating</th>
                <th>Status</th>
                <th>Submitted</th>
                <th>Actions</th>
              </tr>
            </thead>
            <tbody>
              {loading ? (
                <tr>
                  <td colSpan={7} className="md-empty">
                    Loading feedback...
                  </td>
                </tr>
              ) : items.length === 0 ? (
                <tr>
                  <td colSpan={7} className="md-empty">
                    No feedback found.{" "}
                    <button
                      type="button"
                      className="md-link-btn"
                      onClick={() => navigate("/help-center/feedback")}
                    >
                      Submit feedback
                    </button>
                  </td>
                </tr>
              ) : (
                items.map((row) => (
                  <tr key={row.id}>
                    <td>
                      <div className="md-id-cell">
                        <button
                          type="button"
                          className="md-id-link"
                          onClick={() => openDetail(row)}
                        >
                          {row.feedbackNumber}
                        </button>
                        <span className="md-id-chip">#{String(row.id).padStart(6, "0")}</span>
                      </div>
                    </td>
                    <td>
                      <div className="md-stack">
                        <strong title={row.subject}>{truncateText(row.subject, 42)}</strong>
                        <span title={row.description}>{truncateText(row.description, 54)}</span>
                      </div>
                    </td>
                    <td>
                      <div className="md-category">
                        <span className="md-category__icon" aria-hidden>
                          <LuMessageSquareHeart />
                        </span>
                        <span title={row.feedbackType}>{truncateText(row.feedbackType, 28)}</span>
                      </div>
                    </td>
                    <td>{renderStars(row.rating)}</td>
                    <td>
                      <span className={statusClass(row.status)}>
                        {formatStatus(row.status, row.statusLabel)}
                      </span>
                    </td>
                    <td>
                      <div className="md-stack">
                        <strong>{formatDate(row.createdAt)}</strong>
                        <span>{formatTime(row.createdAt)}</span>
                      </div>
                    </td>
                    <td>
                      <button
                        type="button"
                        className="md-icon-btn"
                        title="View details"
                        onClick={() => openDetail(row)}
                      >
                        <LuEye aria-hidden />
                      </button>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>

        <footer className="md-footer">
          <span>
            Showing {items.length ? (currentPage - 1) * pageSize + 1 : 0} to{" "}
            {Math.min(currentPage * pageSize, total)} of {total} feedback
          </span>
          <div className="md-pagination">
            <button
              type="button"
              disabled={currentPage <= 1}
              onClick={() => setPage((value) => Math.max(1, value - 1))}
            >
              <LuChevronLeft aria-hidden />
            </button>
            {pageNumbers.map((item) => (
              <button
                key={item}
                type="button"
                className={currentPage === item ? "is-active" : ""}
                onClick={() => setPage(item)}
              >
                {item}
              </button>
            ))}
            <button
              type="button"
              disabled={currentPage >= totalPages}
              onClick={() => setPage((value) => Math.min(totalPages, value + 1))}
            >
              <LuChevronRight aria-hidden />
            </button>
            <select
              value={pageSize}
              onChange={(event) => {
                setPageSize(Number(event.target.value));
                setPage(1);
              }}
            >
              {PAGE_OPTIONS.map((size) => (
                <option key={size} value={size}>
                  {size} per page
                </option>
              ))}
            </select>
          </div>
        </footer>
      </section>

      <aside className="md-help-banner">
        <div>
          <strong>How feedback works?</strong>
          <p>
            Suggestions and compliments may be closed after review. Issues that need follow-up are
            assigned to our support team.
          </p>
        </div>
        <Link to="/help-center" className="md-btn md-btn--ghost">
          Learn More
        </Link>
      </aside>
    </div>
  );
}
